import React, {
  createContext, useLayoutEffect, useMemo, useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AppLayout from '../../../../../generic-components/Layout';
import { BREADCRUMB_DATA, INITIAL_BILL_DATA_MODEL, STYLES } from '../consts';
import { CreateBillBody } from '../components/CreateBillBody';
import { useStore } from '../../../../../store';
import { ActionFooter } from '../components/ActionFooter';
import { NavigationHeader } from '../../../../../components/NavigationHeader';
import { VIEWS_MAP } from '../../../../../router/constants';
import { BillCreationSuccess } from './BillCreationSuccess';
import Spinner, { SpinnerColor } from '../../../../../generic-components/Spinner';
import { IBillItem } from '../../../types';
import { trackMixPanel } from '../../../../../services/mixpanel.service';
import { LeaveCreationPopup } from '../components/LeaveCreationPopup';
import { BILLS_STATUSES } from '../../../consts';

export const CreationCtx = createContext<{isInitiatedWithBillImage: boolean, setIsLoadingBillAttachment: any, isLoadingBillAttachment: boolean}>({ isInitiatedWithBillImage: false, isLoadingBillAttachment: false, setIsLoadingBillAttachment: () => {} });

export const CreateBill = () => {
  const setBillData = useStore(state => state.setBillData);
  const billData = useStore(state => state.billData);
  const setSelectedVendorId = useStore(state => state.setSelectedVendorId);
  const resetAddBillSlice = useStore(state => state.resetAddBillSlice);
  const unpaidBills = useStore(state => state.unpaidBills);
  const paidBills = useStore(state => state.paidBills);
  const { state: navState } = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isInitiatedWithBillImage, setIsInitiatedWithBillImage] = useState<boolean>(false);
  const [isFlowSucceed, setIsFlowSucceed] = useState<boolean>(false);
  const [isPreLoading, setIsPreLoading] = useState<boolean>(true);
  const [isDeleteConfirmShow, setIsDeleteConfirmShow] = useState<boolean>(false);
  const [isLoadingBillAttachment, setIsLoadingBillAttachment] = useState<boolean>(false);
  const setShowBillsHub = useStore(state => state.setShowBillsHub);

  const ctxValue = useMemo(() => ({
    isInitiatedWithBillImage,
    setIsLoadingBillAttachment,
    isLoadingBillAttachment,
  }), [isInitiatedWithBillImage, isLoadingBillAttachment]);

  const handleCloseFlow = () => {
    trackMixPanel('Act on add new bill', { Action: 'x' });
    setBillData(INITIAL_BILL_DATA_MODEL);
    setSelectedVendorId('');
    navigate(`/${VIEWS_MAP.BILLS.BASE}`);
  };

  const getInitialFormData = () => {
    let data: IBillItem | null = null;
    if (navState.billStatus === BILLS_STATUSES.PENDING || navState.billStatus === BILLS_STATUSES.AWAITING_DETAILS) {
      // eslint-disable-next-line prefer-destructuring
      data = unpaidBills.filter(bill => bill.billId === navState.billId)[0];
    } else {
      // eslint-disable-next-line prefer-destructuring
      data = paidBills.filter(bill => bill.billId === navState.billId)[0];
    }
    if (!data) {
      setBillData(INITIAL_BILL_DATA_MODEL);
    } else {
      if (data.documentId) {
        trackMixPanel('View add a new bill scan');
        setIsInitiatedWithBillImage(true);
      }
      setBillData(
        {
          billId: '',
          amount: data.amount,
          billNumber: '',
          dueDate: new Date(data.dueDate),
          supplierName: data.supplierName,
          supplierId: data.supplierId,
          billImageUrl: '',
          documentId: '',
          assetType: '',
          assetName: '',
          paymentReminderData: null,
          recurringPaymentData: data.recurringPaymentData || null,
        },
      );
    }
    setIsPreLoading(false);
  };

  const handleSuccess = () => {
    setIsFlowSucceed(true);
    setShowBillsHub(true);
  };

  useLayoutEffect(() => {
    if (navState?.billId) getInitialFormData();
    else if (billData.billImageUrl) {
      trackMixPanel('View add a new bill scan');
      setIsInitiatedWithBillImage(true);
      setIsPreLoading(false);
    } else {
      resetAddBillSlice();
      trackMixPanel('View add new bill manually');
      setIsPreLoading(false);
    }
  }, []);

  return (
    <AppLayout
      layoutStyle={STYLES.INDEX.LAYOUT}
      navBarStyle=''
      chatBotStyle='hidden'
    >
      {isDeleteConfirmShow && <LeaveCreationPopup onClosePopup={() => setIsDeleteConfirmShow(false)} onCloseWizard={handleCloseFlow} />}
      {isPreLoading ? <Spinner spinnerColor={SpinnerColor.Blue} height='2rem' width='2rem' /> : isFlowSucceed ? <BillCreationSuccess /> : (
        <CreationCtx.Provider value={ctxValue}>
          <NavigationHeader
            onCloseAction={() => setIsDeleteConfirmShow(true)}
            pageTitle={navState?.billId
              ? t('CREATE_BILL.BREADCRUMB.DUPLICATE_BILL')
              : t('CREATE_BILL.BREADCRUMB.ADD_NEW_BILL')}
            list={BREADCRUMB_DATA(navState?.billId)}
          />
          <CreateBillBody shouldShowImageContainer={isInitiatedWithBillImage} />
          <ActionFooter onSuccess={handleSuccess} />
        </CreationCtx.Provider>
      )}

    </AppLayout>
  );
};
